// Mini-game data, grounded in the repo knowledge base (knowledge_base/*.md —
// Hanna Zakharieva "Color Logic & Perfect Brows", 58-slide deck + 16-page
// notebook). Bilingual (ru/en). Explanations carry NO source references
// («[КБ: 01 · слайд 2]» etc.) — players never see page/slide citations; verify
// a fact against knowledge_base/*.md instead. Pure frontend — no backend needed.

export interface Bilingual {
  ru: string;
  en: string;
}

// ── Quiz ────────────────────────────────────────────────────────────────────
export interface QuizQuestion {
  q: Bilingual;
  options: Bilingual[];
  correct: number; // index into options
  explain: Bilingual;
}

// Stable key per question (hash of the RU text) — used to aggregate answer
// statistics on the backend, surviving reordering of the pool.
export function quizKey(q: QuizQuestion): string {
  const s = q.q.ru;
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return "q" + h.toString(36);
}

export const QUIZ: QuizQuestion[] = [
  // ── 01 · Оборудование и картриджи ────────────────────────────────────────
  {
    q: { ru: "Рабочий вольтаж аппарата ME?", en: "Working voltage of the ME device?" },
    options: [{ ru: "4.0–5.0", en: "4.0–5.0" }, { ru: "6.0–7.0", en: "6.0–7.0" }, { ru: "8.0–9.0", en: "8.0–9.0" }],
    correct: 1,
    explain: { ru: "Аппарат ME: вольтаж 6.0–7.0, движение — маятник.", en: "ME device: voltage 6.0–7.0, motion — pendulum." },
  },
  {
    q: { ru: "Каким движением работаем?", en: "What is the working motion?" },
    options: [{ ru: "Круговое", en: "Circular" }, { ru: "Маятник", en: "Pendulum" }, { ru: "Точечное", en: "Dotting" }],
    correct: 1,
    explain: { ru: "Базовое движение аппарата — маятник.", en: "The base motion is the pendulum." },
  },
  {
    q: { ru: "Какой бренд картриджей используется в курсе?", en: "Which cartridge brand is used in the course?" },
    options: [{ ru: "Cheyenne", en: "Cheyenne" }, { ru: "KWADRON", en: "KWADRON" }, { ru: "Bishop", en: "Bishop" }],
    correct: 1,
    explain: { ru: "Три бренда: KWADRON, CM SUPPLY, VIPER (базовый — KWADRON OPTIMA+).", en: "Three brands: KWADRON, CM SUPPLY, VIPER (base — KWADRON OPTIMA+)." },
  },
  {
    q: { ru: "Для какой кожи подходит мягкая игла?", en: "Which skin is the soft needle for?" },
    options: [{ ru: "Тонкая, сухая", en: "Thin, dry" }, { ru: "Плотная, жирная", en: "Dense, oily" }, { ru: "Пористая", en: "Porous" }],
    correct: 0,
    explain: { ru: "Мягкая игла (напайка высоко/отсутствует) — для тонкой/сухой кожи, бархатный финиш.", en: "Soft needle (weld high/absent) — for thin/dry skin, velvet finish." },
  },
  {
    q: { ru: "Чем ближе напайка картриджа к основанию, тем игла…", en: "The closer the cartridge weld to the base, the needle is…" },
    options: [{ ru: "Мягче", en: "Softer" }, { ru: "Жёстче", en: "Stiffer" }, { ru: "Не меняется", en: "Unchanged" }],
    correct: 1,
    explain: { ru: "Чем ближе напайка к основанию — тем жёстче игла.", en: "The closer the weld to the base, the stiffer the needle." },
  },
  {
    q: { ru: "Как понять жёсткость картриджа?", en: "How do you tell a cartridge's stiffness?" },
    options: [{ ru: "Посмотреть упаковку", en: "Check the packaging" }, { ru: "Разобрать иглу", en: "Take the needle apart" }, { ru: "Измерить вольтаж", en: "Measure voltage" }],
    correct: 1,
    explain: { ru: "Разобрать иглу и посмотреть на напайку.", en: "Take the needle apart and look at the weld." },
  },
  {
    q: { ru: "Минералы, тонкая кожа — какой картридж?", en: "Minerals, thin skin — which cartridge?" },
    options: [{ ru: "0.25\\0.30 1RLLT", en: "0.25\\0.30 1RLLT" }, { ru: "0.35 1RLLT-T", en: "0.35 1RLLT-T" }, { ru: "0.30\\0.35 1RLLT", en: "0.30\\0.35 1RLLT" }],
    correct: 0,
    explain: { ru: "Минералы, тонкая кожа — 0.25\\0.30 1RLLT.", en: "Minerals, thin skin — 0.25\\0.30 1RLLT." },
  },
  {
    q: { ru: "Минералы, плотная/пористая кожа — что даёт быструю укладку?", en: "Minerals, dense/porous skin — what gives a fast layup?" },
    options: [{ ru: "0.25\\0.30 1RLLT", en: "0.25\\0.30 1RLLT" }, { ru: "0.35 1RLLT-T", en: "0.35 1RLLT-T" }, { ru: "0.20 1RL", en: "0.20 1RL" }],
    correct: 1,
    explain: { ru: "Плотная/пористая — 0.35 1RLLT или 0.35 1RLLT-T (быстрая укладка).", en: "Dense/porous — 0.35 1RLLT or 0.35 1RLLT-T (fast layup)." },
  },
  {
    q: { ru: "Гибриды, плотная/пористая кожа — картридж?", en: "Hybrids, dense/porous skin — cartridge?" },
    options: [{ ru: "0.30\\0.35 1RLLT", en: "0.30\\0.35 1RLLT" }, { ru: "0.25\\0.30 1RLLT", en: "0.25\\0.30 1RLLT" }, { ru: "0.35 1RLLT-T", en: "0.35 1RLLT-T" }],
    correct: 0,
    explain: { ru: "Гибриды, плотная/пористая — 0.30\\0.35 1RLLT.", en: "Hybrids, dense/porous — 0.30\\0.35 1RLLT." },
  },
  {
    q: { ru: "Что даёт картридж 0.35 1RLLT на нормальной коже?", en: "What does a 0.35 1RLLT give on normal skin?" },
    options: [{ ru: "Быструю укладку, крупнее пиксель", en: "Fast layup, larger pixel" }, { ru: "Самый тонкий пиксель", en: "The finest pixel" }, { ru: "Минимальный остаток", en: "Minimal residue" }],
    correct: 0,
    explain: { ru: "0.35 1RLLT — быстрая укладка, крупнее пиксель, больший остаток.", en: "0.35 1RLLT — fast layup, larger pixel, more residue." },
  },

  // ── 02 · Цветовая логика ──────────────────────────────────────────────────
  {
    q: { ru: "Из чего строится коричневый?", en: "What is brown built from?" },
    options: [{ ru: "Красный + жёлтый + чёрный", en: "Red + yellow + black" }, { ru: "Синий + красный", en: "Blue + red" }, { ru: "Жёлтый + белый", en: "Yellow + white" }],
    correct: 0,
    explain: { ru: "Коричневый = красный + жёлтый + чёрный.", en: "Brown = red + yellow + black." },
  },
  {
    q: { ru: "Жёлтый + чёрный дают…", en: "Yellow + black give…" },
    options: [{ ru: "Оранжевый", en: "Orange" }, { ru: "Оливковый", en: "Olive" }, { ru: "Фиолетовый", en: "Violet" }],
    correct: 1,
    explain: { ru: "Жёлтый + чёрный = оливковый; жёлтый + красный = оранжевый.", en: "Yellow + black = olive; yellow + red = orange." },
  },
  {
    q: { ru: "Если жёлтого больше, чем красного, цвет будет…", en: "If there is more yellow than red, the colour is…" },
    options: [{ ru: "Горчично-золотистый (тёплый, светлый)", en: "Mustard-gold (warm, light)" }, { ru: "Тёмно-серый", en: "Dark grey" }, { ru: "Холодный синий", en: "Cool blue" }],
    correct: 0,
    explain: { ru: "Жёлтого больше → горчично-золотистый, тёплый и светлый.", en: "More yellow → mustard-gold, warm and light." },
  },

  // ── 03 · Базовые миксы по тону кожи ───────────────────────────────────────
  {
    q: { ru: "Светлая кожа, минералы — базовое соотношение?", en: "Light skin, minerals — base ratio?" },
    options: [{ ru: "80% / 20%", en: "80% / 20%" }, { ru: "20% / 80%", en: "20% / 80%" }, { ru: "50% / 50%", en: "50% / 50%" }],
    correct: 0,
    explain: { ru: "Светлая кожа: минералы 80% / 20%, гибриды 50% / 50%.", en: "Light skin: minerals 80% / 20%, hybrids 50% / 50%." },
  },
  {
    q: { ru: "Светлая кожа, гибриды — соотношение?", en: "Light skin, hybrids — ratio?" },
    options: [{ ru: "50% / 50%", en: "50% / 50%" }, { ru: "80% / 20%", en: "80% / 20%" }, { ru: "20% / 80%", en: "20% / 80%" }],
    correct: 0,
    explain: { ru: "Светлая кожа, гибриды — 50% / 50%.", en: "Light skin, hybrids — 50% / 50%." },
  },
  {
    q: { ru: "Смуглая кожа, гибриды — соотношение?", en: "Tan skin, hybrids — ratio?" },
    options: [{ ru: "20% / 80%", en: "20% / 80%" }, { ru: "80% / 20%", en: "80% / 20%" }, { ru: "50% / 50%", en: "50% / 50%" }],
    correct: 0,
    explain: { ru: "Смуглая кожа: минералы 80% / 20%, гибриды 20% / 80%, + насыщенность.", en: "Tan skin: minerals 80% / 20%, hybrids 20% / 80%, + saturation." },
  },
  {
    q: { ru: "Тёмная кожа, минералы — соотношение?", en: "Dark skin, minerals — ratio?" },
    options: [{ ru: "20% / 50% / 30%", en: "20% / 50% / 30%" }, { ru: "80% / 20%", en: "80% / 20%" }, { ru: "50% / 50%", en: "50% / 50%" }],
    correct: 0,
    explain: { ru: "Тёмная кожа, минералы: 20% / 50% / 30% — Etalon Mix №10, №11.", en: "Dark skin, minerals: 20% / 50% / 30% — Etalon Mix №10, №11." },
  },
  {
    q: { ru: "Чем нейтрализуют холодный остаток на тёмной коже (гибриды)?", en: "What neutralises a cold cast on dark skin (hybrids)?" },
    options: [{ ru: "Оранжевый корректор (Perma Blend)", en: "Orange corrector (Perma Blend)" }, { ru: "Серый корректор", en: "Grey corrector" }, { ru: "Разбавитель", en: "Diluent" }],
    correct: 0,
    explain: { ru: "Тёмная кожа, гибриды — 20/50/30 + оранжевый корректор (Perma Blend): №5, №11, №1.", en: "Dark skin, hybrids — 20/50/30 + orange corrector (Perma Blend): №5, №11, №1." },
  },

  // ── 04 · Перекрытие / коррекция ───────────────────────────────────────────
  {
    q: { ru: "Главный закон перекрытия старого перманента?", en: "The key law of covering old PMU?" },
    options: [{ ru: "Нельзя положить светлое на тёмное", en: "Can't put light over dark" }, { ru: "Любой цвет можно перекрыть", en: "Any colour can be covered" }, { ru: "Светлым перекрывают тёмное", en: "Light covers dark" }],
    correct: 0,
    explain: { ru: "Нельзя положить светлое на тёмное — можно изменить только оттенок и плотность.", en: "You can't put light over dark — only the hue and density can change." },
  },
  {
    q: { ru: "При каком остатке перекрытие возможно?", en: "At what residue is a cover-up possible?" },
    options: [{ ru: "≤ 40% от исходного", en: "≤ 40% of the original" }, { ru: "> 40% от исходного", en: "> 40% of the original" }, { ru: "Любой остаток", en: "Any residue" }],
    correct: 0,
    explain: { ru: "Возможно при остатке ≤ 40%, без рубцов, форма в пределах новой.", en: "Possible at residue ≤ 40%, no scars, shape within the new one." },
  },
  {
    q: { ru: "Зачем нужен тест анестетиком на сером перманенте?", en: "Why the anaesthetic test on grey PMU?" },
    options: [{ ru: "Проявить настоящий цвет остатка", en: "Reveal the true residue colour" }, { ru: "Обезболить навсегда", en: "Numb permanently" }, { ru: "Осветлить пигмент", en: "Lighten the pigment" }],
    correct: 0,
    explain: { ru: "Анестетик проявляет скрытый подтон: STEP1 №11 (тепло), STEP2 анестезия 2–3 мин, STEP3 анализ.", en: "The anaesthetic reveals the hidden undertone: STEP1 №11 (warmth), STEP2 numb 2–3 min, STEP3 analyse." },
  },
  {
    q: { ru: "Зелёный остаток старого пигмента — что добавить в микс?", en: "Green residue of old pigment — what to add to the mix?" },
    options: [{ ru: "№3 или №9", en: "№3 or №9" }, { ru: "Чёрный", en: "Black" }, { ru: "Разбавитель", en: "Diluent" }],
    correct: 0,
    explain: { ru: "Зелёный остаток → добавить №3 или №9 (тёплые красные основы).", en: "Green residue → add №3 or №9 (warm red bases)." },
  },
  {
    q: { ru: "Фиолетовый остаток требует каких корректоров?", en: "Violet residue needs which correctors?" },
    options: [{ ru: "Золотисто-жёлтых", en: "Golden-yellow" }, { ru: "Холодных синих", en: "Cool blue" }, { ru: "Серых", en: "Grey" }],
    correct: 0,
    explain: { ru: "Фиолетовый → золотисто-жёлтые корректоры (фиолет + жёлтый = зелёный → нейтрализация); проход №4+№11.", en: "Violet → golden-yellow correctors (violet + yellow = green → neutralise); pass №4+№11." },
  },

  // ── 05 · Рецепты по типу клиента ──────────────────────────────────────────
  {
    q: { ru: "Рыжие (тёплый золотистый, веснушки) — базовый микс?", en: "Redheads (warm golden, freckles) — base mix?" },
    options: [{ ru: "№6 + №8 (10/5)", en: "№6 + №8 (10/5)" }, { ru: "№1 + №3", en: "№1 + №3" }, { ru: "№10 + №2", en: "№10 + №2" }],
    correct: 0,
    explain: { ru: "Рыжие: №6 + №8 (10/5) или №6 + №11 (10/2); доп. Contour, Aqua.", en: "Redheads: №6 + №8 (10/5) or №6 + №11 (10/2); add-ons Contour, Aqua." },
  },
  {
    q: { ru: "Блонд: чем теплее нужен основной цвет, тем больше добавляют…", en: "Blonde: the warmer the base needed, the more you add of…" },
    options: [{ ru: "№11", en: "№11" }, { ru: "Чёрного", en: "Black" }, { ru: "Разбавителя", en: "Diluent" }],
    correct: 0,
    explain: { ru: "Блонд: №6+№11, №7+№11, Dark Toffee+№11. Теплее цвет → больше №11.", en: "Blonde: №6+№11, №7+№11, Dark Toffee+№11. Warmer → more №11." },
  },
  {
    q: { ru: "Какие пигменты НЕЛЬЗЯ брать как базу для самой светлой кожи?", en: "Which pigments must NOT be the base for the lightest skin?" },
    options: [{ ru: "№3, №11 или №5", en: "№3, №11 or №5" }, { ru: "№6, №8 или №7", en: "№6, №8 or №7" }, { ru: "№10, №2 или №4", en: "№10, №2 or №4" }],
    correct: 0,
    explain: { ru: "Никогда не берите №3, №11 или №5 как базу для самой светлой кожи.", en: "Never use №3, №11 or №5 as the base for the lightest skin." },
  },
  {
    q: { ru: "Фарфоровая / холодная блондинка — микс?", en: "Porcelain / cold blonde — mix?" },
    options: [{ ru: "№1 или №4 + №11 (1:1)", en: "№1 or №4 + №11 (1:1)" }, { ru: "№5 + №11", en: "№5 + №11" }, { ru: "№6 + №8", en: "№6 + №8" }],
    correct: 0,
    explain: { ru: "Фарфоровая / холодная блондинка: №1 или №4 + №11 (1:1).", en: "Porcelain / cold blonde: №1 or №4 + №11 (1:1)." },
  },
  {
    q: { ru: "Карамельная кожа (славянский IV–V) — микс?", en: "Caramel skin (Slavic IV–V) — mix?" },
    options: [{ ru: "№5 + №11", en: "№5 + №11" }, { ru: "№3 + №4", en: "№3 + №4" }, { ru: "№6 + №8", en: "№6 + №8" }],
    correct: 0,
    explain: { ru: "Карамельная кожа: №5 + №11 (теплее → больше №11).", en: "Caramel skin: №5 + №11 (warmer → more №11)." },
  },
  {
    q: { ru: "Светло-оливковая кожа (славянский IV) — основной микс?", en: "Light-olive skin (Slavic IV) — main mix?" },
    options: [{ ru: "№3 + №4 (2:1)", en: "№3 + №4 (2:1)" }, { ru: "№5 + №11", en: "№5 + №11" }, { ru: "№1 + №11", en: "№1 + №11" }],
    correct: 0,
    explain: { ru: "Светло-оливковая: №3 + №4 (2:1); коррекция №6 + №9 (2:1).", en: "Light-olive: №3 + №4 (2:1); correction №6 + №9 (2:1)." },
  },
  {
    q: { ru: "Азиатский тип (тёплый, плотная кожа) — микс?", en: "Asian type (warm, dense skin) — mix?" },
    options: [{ ru: "№4 + №3 + №11", en: "№4 + №3 + №11" }, { ru: "№1 + №11", en: "№1 + №11" }, { ru: "№6 + №8", en: "№6 + №8" }],
    correct: 0,
    explain: { ru: "Азиатский тип: №4 + №3 + №11.", en: "Asian type: №4 + №3 + №11." },
  },
  {
    q: { ru: "Для оливковой кожи какой пигмент должен преобладать?", en: "For olive skin, which pigment must dominate?" },
    options: [{ ru: "Жёлто-зелёный (над тёплым красным)", en: "Yellow-green (over warm red)" }, { ru: "Холодный синий", en: "Cool blue" }, { ru: "Чёрный", en: "Black" }],
    correct: 0,
    explain: { ru: "Для тёмной/оливковой кожи жёлто-зелёный должен преобладать над тёплым красным, особенно в минералах.", en: "For dark/olive skin the yellow-green must dominate over warm red, especially in minerals." },
  },
  {
    q: { ru: "Брюнетки (жгучий тёмный волос) — один из миксов?", en: "Brunettes (intense dark hair) — one of the mixes?" },
    options: [{ ru: "Dark Toffee + Navadgio Brown (70/30)", en: "Dark Toffee + Navadgio Brown (70/30)" }, { ru: "№6 + №8", en: "№6 + №8" }, { ru: "№3 + №4", en: "№3 + №4" }],
    correct: 0,
    explain: { ru: "Брюнетки: Dark Toffee + Navadgio Brown (70/30); №4 + Espresso (60/40); №6+№10+№11.", en: "Brunettes: Dark Toffee + Navadgio Brown (70/30); №4 + Espresso (60/40); №6+№10+№11." },
  },

  // ── 06 · Пигмент №11 ──────────────────────────────────────────────────────
  {
    q: { ru: "Какую роль играет пигмент №11 в миксе?", en: "What role does pigment №11 play in a mix?" },
    options: [{ ru: "Тёплый универсальный — добавляет тепло", en: "Warm all-rounder — adds warmth" }, { ru: "Охлаждает микс", en: "Cools the mix" }, { ru: "Осветляет кожу", en: "Lightens the skin" }],
    correct: 0,
    explain: { ru: "№11 — тёплый универсальный пигмент: чем теплее нужен результат, тем больше №11.", en: "№11 is a warm all-rounder: the warmer the result needed, the more №11." },
  },

  // ── 07 · Техники ──────────────────────────────────────────────────────────
  {
    q: { ru: "Для кого подходит Powder Brows «Схема №1»?", en: "Who is Powder Brows 'Scheme #1' for?" },
    options: [{ ru: "Пудровые техники / гибридные пигменты", en: "Powder techniques / hybrid pigments" }, { ru: "Только волосковая техника", en: "Hair-stroke technique only" }, { ru: "Только тёмная кожа", en: "Dark skin only" }],
    correct: 0,
    explain: { ru: "Схема №1 — для пудровых техник или гибридных пигментов; работа плоским маятником.", en: "Scheme #1 — for powder techniques or hybrid pigments; flat-pendulum work." },
  },
  {
    q: { ru: "Длина горизонтального штриха в STEP 1 (Схема №1)?", en: "Horizontal stroke length in STEP 1 (Scheme #1)?" },
    options: [{ ru: "~8–9 мм", en: "~8–9 mm" }, { ru: "~2–3 см", en: "~2–3 cm" }, { ru: "~1 мм", en: "~1 mm" }],
    correct: 0,
    explain: { ru: "STEP 1 — горизонтальные штрихи в две стороны ~8–9 мм, средней плотности.", en: "STEP 1 — horizontal strokes both ways ~8–9 mm, medium density." },
  },
  {
    q: { ru: "Длина штриха в STEP 3 (градиент, Схема №1)?", en: "Stroke length in STEP 3 (gradient, Scheme #1)?" },
    options: [{ ru: "~5–7 мм", en: "~5–7 mm" }, { ru: "~1–2 см", en: "~1–2 cm" }, { ru: "~0.5 мм", en: "~0.5 mm" }],
    correct: 0,
    explain: { ru: "STEP 3 — горизонтальные штрихи ~5–7 мм и диагональные; можно за форму на 2–3 мм.", en: "STEP 3 — horizontal strokes ~5–7 mm and diagonal; up to 2–3 mm beyond the shape." },
  },
  {
    q: { ru: "Что будет, если задержаться на верхней границе брови?", en: "What happens if you linger on the upper brow border?" },
    options: [{ ru: "Получим «лысину»", en: "You get a 'bald spot'" }, { ru: "Цвет станет теплее", en: "Colour gets warmer" }, { ru: "Ничего", en: "Nothing" }],
    correct: 0,
    explain: { ru: "На верхней границе не задерживаемся, иначе «лысина». Работаем пудровым штрихом.", en: "Don't linger on the upper border or you get a 'bald spot'. Work with a powder stroke." },
  },
  {
    q: { ru: "Какое наслоение при фиксации эскиза (Powder for Ladies)?", en: "What layering when fixing the sketch (Powder for Ladies)?" },
    options: [{ ru: "50/50", en: "50/50" }, { ru: "70/30", en: "70/30" }, { ru: "100/0", en: "100/0" }],
    correct: 0,
    explain: { ru: "Наслоение 50/50, движения 0.7–1 см «на себя» либо маятник, без закрепления контура.", en: "Layering 50/50, 0.7–1 cm 'toward yourself' or pendulum, no contour fixing." },
  },
  {
    q: { ru: "Сколько проходов делают в центре (теле) брови в Ombré?", en: "How many passes in the brow center (body) in Ombré?" },
    options: [{ ru: "4P", en: "4P" }, { ru: "2P", en: "2P" }, { ru: "6P", en: "6P" }],
    correct: 0,
    explain: { ru: "Проходы по зонам: начало 2P, тело/центр 4P, хвост 4P.", en: "Passes per zone: head 2P, body/center 4P, tail 4P." },
  },
  {
    q: { ru: "Где укладывают максимум пигмента при оттушёвке?", en: "Where is the most pigment placed during shading?" },
    options: [{ ru: "В центре, акцент к нижней границе", en: "In the center, accent to the lower edge" }, { ru: "В начале брови", en: "At the brow head" }, { ru: "На верхней границе", en: "On the upper border" }],
    correct: 0,
    explain: { ru: "Центр — максимум пигмента; начало разрежённое, почти прозрачное; вертикальные штрихи.", en: "Center — max pigment; head sparse, almost transparent; vertical strokes." },
  },
  {
    q: { ru: "Какой отступ от точек построения при эскизе?", en: "What offset from the construction points when sketching?" },
    options: [{ ru: "+5–6 мм", en: "+5–6 mm" }, { ru: "+1 см", en: "+1 cm" }, { ru: "0 мм", en: "0 mm" }],
    correct: 0,
    explain: { ru: "Размечаем точки и направления, отступ +5–6 мм от каждой точки.", en: "Mark points and directions, offset +5–6 mm from each point." },
  },

  // ── 08 · Уход и исправления ───────────────────────────────────────────────
  {
    q: { ru: "Что делать в 1-й день после процедуры?", en: "What to do on day 1?" },
    options: [{ ru: "Промывать 2–3 раза водой с мылом", en: "Wash 2–3× with soap & water" }, { ru: "Не трогать сутки", en: "Don't touch for a day" }, { ru: "Распарить", en: "Steam it" }],
    correct: 0,
    explain: { ru: "1 день: промывать 2–3 раза в день водой с мылом или кипячёной.", en: "Day 1: wash 2–3× a day with soap & water or boiled water." },
  },
  {
    q: { ru: "С какого дня можно умывать брови?", en: "From which day can brows be washed?" },
    options: [{ ru: "3–5 день", en: "Days 3–5" }, { ru: "1 день", en: "Day 1" }, { ru: "10–11 день", en: "Days 10–11" }],
    correct: 0,
    explain: { ru: "3–5 день — умывать промакивающими движениями (не тереть), не распаривать.", en: "Days 3–5 — wash with blotting (don't rub), don't steam." },
  },
  {
    q: { ru: "Когда сходят корочки?", en: "When do the crusts shed?" },
    options: [{ ru: "6–9 день", en: "Days 6–9" }, { ru: "1–2 день", en: "Days 1–2" }, { ru: "14–16 день", en: "Days 14–16" }],
    correct: 0,
    explain: { ru: "6–9 день — схождение корочек, мочить можно.", en: "Days 6–9 — crusts shed, getting wet is fine." },
  },
  {
    q: { ru: "Через сколько проявляется итоговый цвет?", en: "After how long does the final colour appear?" },
    options: [{ ru: "Через 3–4 недели", en: "In 3–4 weeks" }, { ru: "Через 2–3 дня", en: "In 2–3 days" }, { ru: "Через полгода", en: "In six months" }],
    correct: 0,
    explain: { ru: "10–11 день цвет бледный; итоговый проявится через 3–4 недели.", en: "On days 10–11 the colour is pale; the final shows in 3–4 weeks." },
  },
  {
    q: { ru: "Неравномерное схождение пигмента: с чего начинаем коррекцию?", en: "Uneven pigment retention: how do you start the correction?" },
    options: [{ ru: "Горизонтально по участкам с меньшим запалом", en: "Horizontally over weakly-retained areas" }, { ru: "Вертикально по всей брови", en: "Vertically over the whole brow" }, { ru: "Точками по краям", en: "Dots on the edges" }],
    correct: 0,
    explain: { ru: "Первый проход — по участкам, куда меньше «запал» пигмент (горизонтально).", en: "First pass — over areas where pigment took less (horizontal)." },
  },
  {
    q: { ru: "Клиент хочет ярче — что используем?", en: "Client wants brighter — what do we use?" },
    options: [{ ru: "Минералы/гибриды с большей плотностью", en: "Minerals/hybrids with more density" }, { ru: "Только разбавитель", en: "Diluent only" }, { ru: "Меньший вольтаж", en: "Lower voltage" }],
    correct: 0,
    explain: { ru: "Клиент хочет ярче — минеральные или гибридные пигменты с большей плотностью укладки.", en: "Client wants brighter — minerals or hybrids with denser layup." },
  },

  // ── 09 · Цветовая база (Etalon Mix) ──────────────────────────────────────
  {
    q: { ru: "Жёлтый + красный = ?", en: "Yellow + red = ?" },
    options: [{ ru: "Оранжевый", en: "Orange" }, { ru: "Коричневый", en: "Brown" }, { ru: "Фиолетовый", en: "Violet" }],
    correct: 0,
    explain: { ru: "Жёлтый + красный дают оранжевый; коричневый получится, если добавить чёрный.", en: "Yellow + red make orange; add black to get brown." },
  },
  {
    q: { ru: "Что добавить к оранжевому, чтобы получить коричневый?", en: "What do you add to orange to get brown?" },
    options: [{ ru: "Чёрный", en: "Black" }, { ru: "Белый", en: "White" }, { ru: "Красный", en: "Red" }],
    correct: 0,
    explain: { ru: "Оранжевый + чёрный = коричневый — на этом построено перекрытие оранжевых остатков.", en: "Orange + black = brown — this is how orange residues are covered." },
  },
  {
    q: { ru: "Жёлтый + чёрный = ?", en: "Yellow + black = ?" },
    options: [{ ru: "Зелёный", en: "Green" }, { ru: "Коричневый", en: "Brown" }, { ru: "Серый", en: "Grey" }],
    correct: 0,
    explain: { ru: "Неожиданно, но жёлтый + чёрный дают зелёный; до коричневого его доводит красный.", en: "Surprisingly, yellow + black make green; red brings it to brown." },
  },
  {
    q: { ru: "Что добавить к фиолетовому, чтобы получить коричневый?", en: "What do you add to violet to get brown?" },
    options: [{ ru: "Жёлтый", en: "Yellow" }, { ru: "Чёрный", en: "Black" }, { ru: "Красный", en: "Red" }],
    correct: 0,
    explain: { ru: "Фиолетовый (красный + чёрный) + жёлтый = коричневый — логика перекрытия фиолетовых остатков.", en: "Violet (red + black) + yellow = brown — the logic of covering violet residues." },
  },
  {
    q: { ru: "Из каких первичных цветов состоят все пигменты для бровей?", en: "Which primaries make up all brow pigments?" },
    options: [{ ru: "Красный, жёлтый, чёрный", en: "Red, yellow, black" }, { ru: "Красный, синий, жёлтый", en: "Red, blue, yellow" }, { ru: "Коричневый, чёрный, белый", en: "Brown, black, white" }],
    correct: 0,
    explain: { ru: "Все пигменты состоят из 3 первичных цветов: красного, жёлтого и чёрного; любой коричневый — их пропорция.", en: "All pigments are built from 3 primaries: red, yellow and black; any brown is their ratio." },
  },
  {
    q: { ru: "Что преобладает в светлых/нейтральных коричневых №6, №1, №4?", en: "What dominates the light/neutral browns №6, №1, №4?" },
    options: [{ ru: "Жёлтый", en: "Yellow" }, { ru: "Красный", en: "Red" }, { ru: "Чёрный", en: "Black" }],
    correct: 0,
    explain: { ru: "№6/№1/№4 — светлые/нейтральные коричневые: больше жёлтого.", en: "№6/№1/№4 are the light/neutral browns: more yellow." },
  },
  {
    q: { ru: "К какой группе относятся №10 и №5?", en: "Which group do №10 and №5 belong to?" },
    options: [{ ru: "Холодные тёмно-коричневые (больше чёрного)", en: "Cold dark browns (more black)" }, { ru: "Тёплые (больше красного)", en: "Warm (more red)" }, { ru: "Светлые (больше жёлтого)", en: "Light (more yellow)" }],
    correct: 0,
    explain: { ru: "№10 и №5 — холодные тёмно-коричневые, в них преобладает чёрный.", en: "№10 and №5 are the cold dark browns — black dominates." },
  },
  {
    q: { ru: "Из каких двух частей состоит пигмент?", en: "What two parts does a pigment consist of?" },
    options: [{ ru: "Сухие вещества + жидкая основа", en: "Dry substances + liquid base" }, { ru: "Краска + вода", en: "Paint + water" }, { ru: "Масло + порошок", en: "Oil + powder" }],
    correct: 0,
    explain: { ru: "Пигмент = сухие вещества (цвет, стойкость, стабильность) + жидкая основа (стабильная суспензия).", en: "Pigment = dry substances (colour, durability, stability) + a liquid base (stable suspension)." },
  },
  {
    q: { ru: "Коды Color Index неорганических веществ начинаются на…", en: "Color Index codes of inorganic substances start with…" },
    options: [{ ru: "77", en: "77" }, { ru: "11", en: "11" }, { ru: "21", en: "21" }],
    correct: 0,
    explain: { ru: "Неорганика маркируется CI на 77; органика — 11, 12, 21, 56, 73 и др. (но не 77).", en: "Inorganics carry CI codes starting 77; organics — 11, 12, 21, 56, 73 etc. (never 77)." },
  },
  {
    q: { ru: "Гибридный пигмент — это…", en: "A hybrid pigment is…" },
    options: [{ ru: "Органические лаки, осаждённые на соли металлов", en: "Organic lacquers precipitated onto metal salts" }, { ru: "Смесь органики и неорганики", en: "A blend of organics and inorganics" }, { ru: "Минерал с блёстками", en: "A mineral with shimmer" }],
    correct: 0,
    explain: { ru: "Гибриды — это НЕ смесь органики с неорганикой, а цветные органические лаки на солях металлов.", en: "Hybrids are NOT a blend of organic and inorganic — they're coloured organic lacquers on metal salts." },
  },
  // ── 09 · Поведение пигментов в коже ──────────────────────────────────────
  {
    q: { ru: "Как ведут себя органика и гибриды в носке?", en: "How do organics and hybrids behave over time?" },
    options: [{ ru: "Холоднеют/сереют", en: "Cool down / grey out" }, { ru: "Теплеют/рыжеют", en: "Warm up / turn orange" }, { ru: "Не меняются", en: "Don't change" }],
    correct: 0,
    explain: { ru: "Органика и гибриды идут на понижение температуры: красный и жёлтый частично утилизируются, стойкий carbon black остаётся — микс сереет.", en: "Organics/hybrids drift cooler: red and yellow partly metabolise, durable carbon black stays — the mix greys out." },
  },
  {
    q: { ru: "Как ведут себя минеральные пигменты в носке?", en: "How do mineral pigments behave over time?" },
    options: [{ ru: "Теплеют (уходят в красный)", en: "Warm up (drift to red)" }, { ru: "Синеют", en: "Turn blue" }, { ru: "Сереют", en: "Grey out" }],
    correct: 0,
    explain: { ru: "Минералы теплеют: чёрный оксид железа 77499 меняет валентность и становится красным уже через 3–8 месяцев.", en: "Minerals warm up: black iron oxide 77499 changes valence and turns red within 3–8 months." },
  },
  {
    q: { ru: "Какая линия предпочтительнее для тонкой/возрастной кожи и новичков?", en: "Which line is preferable for thin/mature skin and beginners?" },
    options: [{ ru: "Минеральная", en: "Mineral" }, { ru: "Гибридная", en: "Hybrid" }, { ru: "Любая", en: "Either" }],
    correct: 0,
    explain: { ru: "Минералы — для тонкой/возрастной кожи и для работы новичков.", en: "Minerals — for thin/mature skin and for beginners." },
  },
  {
    q: { ru: "Какая линия предпочтительнее для перекрытия старого перманента?", en: "Which line is preferable for covering old PMU?" },
    options: [{ ru: "Органика/гибриды", en: "Organics/hybrids" }, { ru: "Минералы", en: "Minerals" }, { ru: "Разбавитель", en: "Diluent" }],
    correct: 0,
    explain: { ru: "Для перекрытия — органика/гибриды: укрывистые, яркие, почти не теряют цвет при заживлении.", en: "For cover-ups — organics/hybrids: opaque, bright, almost no colour loss on healing." },
  },
  {
    q: { ru: "Можно ли добавить органику в минеральный микс?", en: "Can you add organics into a mineral mix?" },
    options: [{ ru: "Нет — будет пятнистость", en: "No — it goes blotchy" }, { ru: "Да, всегда", en: "Yes, always" }, { ru: "Только 50/50", en: "Only 50/50" }],
    correct: 0,
    explain: { ru: "Нельзя: органика останется яркой, а минеральная часть потеряет до 60–70% цвета — работа станет пятнистой.", en: "No: the organic part stays bright while the mineral part loses up to 60–70% of colour — blotchy result." },
  },
  {
    q: { ru: "Какой чёрный мигрирует в глубокие слои дермы и «синеет»?", en: "Which black migrates into deep dermis and 'turns blue'?" },
    options: [{ ru: "Carbon black CI 77266", en: "Carbon black CI 77266" }, { ru: "Оксид железа CI 77499", en: "Iron oxide CI 77499" }, { ru: "Диоксид титана CI 77891", en: "Titanium dioxide CI 77891" }],
    correct: 0,
    explain: { ru: "Carbon black 77266 — мельчайшие наночастицы: мигрирует вглубь, и чем глубже — тем синее мы его видим.", en: "Carbon black 77266 is a nanoparticle: it migrates deep, and the deeper it sits, the bluer it reads." },
  },
  {
    q: { ru: "Самое стабильное неорганическое вещество?", en: "The most stable inorganic substance?" },
    options: [{ ru: "Диоксид титана (77891)", en: "Titanium dioxide (77891)" }, { ru: "Оксид железа жёлтый (77492)", en: "Yellow iron oxide (77492)" }, { ru: "Коричневые оксиды (77480)", en: "Brown oxides (77480)" }],
    correct: 0,
    explain: { ru: "TiO2 77891 — крайне стабильный (поэтому и лазером почти не удаляется); коричневый 77480 — мало стабилен.", en: "TiO2 77891 is extremely stable (which is why laser barely removes it); brown 77480 is poorly stable." },
  },
  {
    q: { ru: "Пигмент «стабилен», если брови со временем…", en: "A pigment is 'stable' if over time the brows…" },
    options: [{ ru: "Бледнеют, но остаются коричневыми", en: "Fade but stay brown" }, { ru: "Уходят в красный/синий", en: "Drift red/blue" }, { ru: "Не меняются вообще", en: "Don't change at all" }],
    correct: 0,
    explain: { ru: "Стабильность — сохранение цвета без ухода в нежелательные оттенки; стойкость — само время жизни в коже.", en: "Stability = keeping the colour without drifting to unwanted hues; durability = how long it physically stays." },
  },
  // ── 09 · Подбор микса по типу кожи ───────────────────────────────────────
  {
    q: { ru: "Чего «не хватает» светлой коже (1–2 тип)?", en: "What does light skin (type 1–2) 'lack'?" },
    options: [{ ru: "Тёплого — добавляем оранжевый или жёлтый", en: "Warmth — add orange or yellow" }, { ru: "Холодного — добавляем чёрный", en: "Coolness — add black" }, { ru: "Ничего", en: "Nothing" }],
    correct: 0,
    explain: { ru: "Светлой коже не хватает тёплого: в зависимости от желаемого результата добавляем оранжевый либо жёлтый.", en: "Light skin lacks warmth: depending on the target we add orange or yellow." },
  },
  {
    q: { ru: "Гибридная база для светлой кожи?", en: "The hybrid base for light skin?" },
    options: [{ ru: "№1 или №4", en: "№1 or №4" }, { ru: "№3 или №11", en: "№3 or №11" }, { ru: "№5", en: "№5" }],
    correct: 0,
    explain: { ru: "Светлая кожа, гибриды: основной №1 или №4, добавочный №11 или №3. Красный доминант как база запрещён.", en: "Light skin, hybrids: base №1 or №4, additive №11 or №3. A red-dominant base is banned." },
  },
  {
    q: { ru: "Зелёные/чайные/медовые глаза на смуглой коже — какая основа микса?", en: "Green/tea/honey eyes on tan skin — which mix base?" },
    options: [{ ru: "Красная (№3/№11, минералы №9/№8)", en: "Red (№3/№11, minerals №9/№8)" }, { ru: "Жёлтая", en: "Yellow" }, { ru: "Чёрная", en: "Black" }],
    correct: 0,
    explain: { ru: "При зелёных, чайных и медовых глазах на смуглой коже берём пигменты на красной основе.", en: "With green, tea or honey eyes on tan skin we take red-base pigments." },
  },
  {
    q: { ru: "Что делает купероз с типом кожи?", en: "What does couperose do to the skin type?" },
    options: [{ ru: "Смещает его в фиолетовый — нужна жёлтая основа", en: "Shifts it violet — a yellow base is needed" }, { ru: "Ничего не меняет", en: "Changes nothing" }, { ru: "Делает кожу темнее на тип", en: "Makes skin one type darker" }],
    correct: 0,
    explain: { ru: "Кожа с куперозом уходит в фиолетовый тип — работаем пигментами на жёлтой основе.", en: "Couperose skin shifts to a violet type — we work with yellow-base pigments." },
  },
  {
    q: { ru: "Микс для тёмной кожи (6 тип)?", en: "The mix for dark skin (type 6)?" },
    options: [{ ru: "№5 + оранжевый корректор 50/50", en: "№5 + orange corrector 50/50" }, { ru: "№1 + разбавитель", en: "№1 + diluent" }, { ru: "Чистый №3", en: "Pure №3" }],
    correct: 0,
    explain: { ru: "Тёмная кожа (глаза всегда чёрные): не хватает тепла — №5 + оранжевый корректор в пропорции 50/50.", en: "Dark skin (eyes always black): lacking warmth — №5 + orange corrector at 50/50." },
  },
  // ── 09 · Любимые пигменты №1/№3/№5 ───────────────────────────────────────
  {
    q: { ru: "Роль №1 Hazelnut в любимом миксе №1+№3+№5?", en: "The role of №1 Hazelnut in the favourite mix №1+№3+№5?" },
    options: [{ ru: "Всегда основа", en: "Always the base" }, { ru: "Только затемнение", en: "Darkening only" }, { ru: "Корректор холода", en: "Cold corrector" }],
    correct: 0,
    explain: { ru: "№1 — всегда основа; №3 — коричневость подтона; №5 — только затемнение. Пропорции индивидуальны.", en: "№1 is always the base; №3 — brownness; №5 — darkening only. Ratios are individual." },
  },
  {
    q: { ru: "Чем больше №3 Cognac в миксе, тем…", en: "The more №3 Cognac in the mix, the…" },
    options: [{ ru: "Коричневее/теплее подтон", en: "Browner/warmer the undertone" }, { ru: "Холоднее микс", en: "Cooler the mix" }, { ru: "Светлее результат", en: "Lighter the result" }],
    correct: 0,
    explain: { ru: "№3 — красная основа: чем его больше, тем коричневее и теплее подтон микса.", en: "№3 is red-based: the more of it, the browner and warmer the mix undertone." },
  },
  {
    q: { ru: "Можно ли работать №5 Dark Chocolate в чистом виде?", en: "Can №5 Dark Chocolate be used pure?" },
    options: [{ ru: "Нет — только в миксах для затемнения", en: "No — only in mixes, for darkening" }, { ru: "Да, на тёмной коже", en: "Yes, on dark skin" }, { ru: "Да, на светлой коже", en: "Yes, on light skin" }],
    correct: 0,
    explain: { ru: "№5 в чистом виде не используется: максимально холодный, требует оранжевого; в миксах — для затемнения и охлаждения.", en: "№5 is never used pure: maximally cold, needs orange; in mixes it darkens and cools." },
  },
  {
    q: { ru: "Что даёт чистый №3 Cognac на холодной коже?", en: "What does pure №3 Cognac give on cold skin?" },
    options: [{ ru: "Фиолетовый подтон", en: "A violet undertone" }, { ru: "Оливковый оттенок", en: "An olive tint" }, { ru: "Идеальный коричневый", en: "A perfect brown" }],
    correct: 0,
    explain: { ru: "№3 в чистом виде на холодной коже может дать фиолетовый подтон — сочетайте его с жёлтой основой (№1/№4).", en: "Pure №3 on cold skin can give a violet undertone — pair it with a yellow base (№1/№4)." },
  },
  // ── 10 · Перекрытие старого перманента ───────────────────────────────────
  {
    q: { ru: "Чем перекрываем серый остаток?", en: "How do we cover a grey residue?" },
    options: [{ ru: "Orange + №2 Milk Chocolate (или №11)", en: "Orange + №2 Milk Chocolate (or №11)" }, { ru: "Чистым №5", en: "Pure №5" }, { ru: "Terracotta + №3", en: "Terracotta + №3" }],
    correct: 0,
    explain: { ru: "Серый — холодный остаток: нейтрализуем теплом — Orange + №2 Milk Chocolate или Orange + №11 Espresso.", en: "Grey is a cold residue: neutralise with warmth — Orange + №2 Milk Chocolate or Orange + №11 Espresso." },
  },
  {
    q: { ru: "Чем перекрываем зелёный (с белым) остаток?", en: "How do we cover a green (with white) residue?" },
    options: [{ ru: "Terracotta + №3 Cognac (или №11)", en: "Terracotta + №3 Cognac (or №11)" }, { ru: "Yellow + Orange", en: "Yellow + Orange" }, { ru: "№5 Dark Chocolate", en: "№5 Dark Chocolate" }],
    correct: 0,
    explain: { ru: "Зелёный гасится красным: Terracotta + тёплая база (№3 или №11) — зелёный + красный = коричневый.", en: "Green is killed by red: Terracotta + a warm base (№3 or №11) — green + red = brown." },
  },
  {
    q: { ru: "Чем перекрываем оранжевый остаток?", en: "How do we cover an orange residue?" },
    options: [{ ru: "№5 Dark Chocolate", en: "№5 Dark Chocolate" }, { ru: "Yellow + №1", en: "Yellow + №1" }, { ru: "Orange + №2", en: "Orange + №2" }],
    correct: 0,
    explain: { ru: "Оранжевый + тёмный холодный = коричневый: достаточно №5 Dark Chocolate.", en: "Orange + a dark cool = brown: №5 Dark Chocolate alone is enough." },
  },
  {
    q: { ru: "Чем перекрываем фиолетовый остаток?", en: "How do we cover a violet residue?" },
    options: [{ ru: "Yellow + Orange + №2 Milk Chocolate", en: "Yellow + Orange + №2 Milk Chocolate" }, { ru: "№5 + №10", en: "№5 + №10" }, { ru: "Terracotta + №5", en: "Terracotta + №5" }],
    correct: 0,
    explain: { ru: "Фиолетовый + жёлтый = коричневый, поэтому Yellow + Orange + №2 Milk Chocolate.", en: "Violet + yellow = brown, hence Yellow + Orange + №2 Milk Chocolate." },
  },
  {
    q: { ru: "Брови из нескольких тонов — как перекрываем?", en: "Brows with several tones — how do we cover?" },
    options: [{ ru: "Каждый цветной участок отдельно", en: "Each coloured zone separately" }, { ru: "Одним миксом по всей брови", en: "One mix over the whole brow" }, { ru: "Только лазером", en: "Laser only" }],
    correct: 0,
    explain: { ru: "Если брови состоят из нескольких тонов — работаем на каждом цветном участке отдельно.", en: "If the brows hold several tones — work each coloured zone separately." },
  },
  {
    q: { ru: "Меняем форму при перекрытии: чем работаем на чистых участках?", en: "Changing the shape during a cover-up: what drives the clean areas?" },
    options: [{ ru: "Цветотип клиента + целевой цвет", en: "The client's colour type + the target colour" }, { ru: "Принесённый цвет", en: "The incoming colour" }, { ru: "Всегда №1", en: "Always №1" }],
    correct: 0,
    explain: { ru: "На зоне перекрытия — по принесённому цвету; на новой форме (чистых участках) — по цветотипу клиента и цвету, к которому приводим перекрытие.", en: "The covered zone follows the incoming colour; the new shape (clean areas) follows the client's colour type and the target colour." },
  },
  // ── 10 · Схемы укладки ───────────────────────────────────────────────────
  {
    q: { ru: "Длина штриха в первом проходе пудровых бровей?", en: "Stroke length in the first pass of powder brows?" },
    options: [{ ru: "8–10 мм", en: "8–10 mm" }, { ru: "2–3 мм", en: "2–3 mm" }, { ru: "15–20 мм", en: "15–20 mm" }],
    correct: 0,
    explain: { ru: "Работа в две стороны, штрих 8–10 мм; в шаге градиента — легче и короче, 5–7 мм.", en: "Work both directions, 8–10 mm strokes; in the gradient step — lighter and shorter, 5–7 mm." },
  },
  {
    q: { ru: "На сколько можно выходить за форму при укладке?", en: "How far may strokes overshoot the shape?" },
    options: [{ ru: "На 2–3 мм — не бойтесь", en: "By 2–3 mm — don't fear it" }, { ru: "Ни на сколько", en: "Not at all" }, { ru: "На 1 см", en: "By 1 cm" }],
    correct: 0,
    explain: { ru: "Не бойтесь выходить за форму на 2–3 мм — штрих становится длиннее и мягче.", en: "Don't fear overshooting by 2–3 mm — the stroke gets longer and softer." },
  },
  {
    q: { ru: "Почему нельзя задерживаться на верхней границе брови?", en: "Why not linger at the brow's upper border?" },
    options: [{ ru: "Получится жёсткая линия", en: "You'll get a hard line" }, { ru: "Пигмент не приживётся", en: "Pigment won't take" }, { ru: "Кожа порвётся", en: "The skin will tear" }],
    correct: 0,
    explain: { ru: "На верхней границе не задерживаемся — иначе вместо мягкой границы получим линию.", en: "Don't linger at the upper border — you'll get a line instead of a soft edge." },
  },
  {
    q: { ru: "Где зона яркости в декоративном градиенте?", en: "Where is the brightness zone in the decorative gradient?" },
    options: [{ ru: "Нижняя треть брови", en: "The lower third of the brow" }, { ru: "Верхняя граница", en: "The upper border" }, { ru: "Начало брови", en: "The brow head" }],
    correct: 0,
    explain: { ru: "В декоративном градиенте зона яркости — только нижняя треть; хвостовую часть прорабатываем 2 раза.", en: "In the decorative gradient the brightness zone is only the lower third; the tail is worked twice." },
  },
  {
    q: { ru: "Градиент с акцентом на середину: где максимум пигмента?", en: "Centre-accent gradient: where is the pigment maximum?" },
    options: [{ ru: "В центре брови", en: "In the brow centre" }, { ru: "В начале брови", en: "In the head" }, { ru: "В хвостике", en: "In the tail" }],
    correct: 0,
    explain: { ru: "Центр усиливается максимально; хвостик чёткий, но не перегруженный; начало — разреженное напыление, верх головки «рассыпчатый».", en: "The centre gets the maximum; the tail is crisp but not overloaded; the head is sparse with a 'crumbly' top edge." },
  },
  {
    q: { ru: "Как начинается схема «Градиент с акцентом на середину»?", en: "How does the centre-accent gradient scheme start?" },
    options: [{ ru: "Эскиз: точка A выше точки C, фиксация горизонтальными штрихами", en: "Sketch: point A above point C, fixed with horizontal strokes" }, { ru: "С контура", en: "With the contour" }, { ru: "С хвоста", en: "With the tail" }],
    correct: 0,
    explain: { ru: "Нарисуйте бровь (точка A выше точки C) и зафиксируйте эскиз горизонтальными штрихами по всей длине, наслоение 50/50, без закрепления контура.", en: "Draw the brow (point A above point C) and fix the sketch with horizontal strokes along the full length, 50/50 layering, no contour setting." },
  },
];

// ── Stroke trainer ────────────────────────────────────────────────────────────
// Guide paths live in a 0..100 (x) × 0..60 (y) coordinate space.
export interface StrokePattern {
  key: string;
  name: Bilingual;
  hint: Bilingual;
  path: [number, number][];
}

export const STROKES: StrokePattern[] = [
  {
    key: "pendulum",
    name: { ru: "Плоский маятник", en: "Flat pendulum" },
    hint: { ru: "Мягкий вход и выход — равномерное колебание по горизонтали.", en: "Soft in and out — even horizontal oscillation." },
    path: [
      [8, 30], [16, 24], [24, 36], [32, 24], [40, 36], [48, 24],
      [56, 36], [64, 24], [72, 36], [80, 24], [88, 30],
    ],
  },
  {
    key: "horizontal",
    name: { ru: "Горизонтальный штрих", en: "Horizontal stroke" },
    hint: { ru: "Ровная линия, работа в две стороны, штрих 8–9 мм.", en: "Even line, work both directions, stroke 8–9 mm." },
    path: [[10, 30], [30, 30], [50, 30], [70, 30], [90, 30]],
  },
  {
    key: "diagonal",
    name: { ru: "Диагональный штрих", en: "Diagonal stroke" },
    hint: { ru: "Плавная диагональ снизу-вверх, не выходя за форму.", en: "Smooth bottom-up diagonal, staying within the shape." },
    path: [[14, 50], [33, 41], [52, 31], [71, 21], [88, 13]],
  },
  {
    key: "fan",
    name: { ru: "Веерный штрих (дуга брови)", en: "Fan stroke (brow arch)" },
    hint: { ru: "Дуга по форме брови: подъём к телу и мягкий спуск к хвосту.", en: "Arc along the brow: rise to the body, soft descent to the tail." },
    path: [
      [10, 46], [22, 38], [34, 30], [46, 23], [56, 21],
      [66, 24], [76, 30], [85, 38], [92, 44],
    ],
  },
  {
    key: "gradient",
    name: { ru: "Градиент-дуга", en: "Gradient arch" },
    hint: { ru: "Лёгкий проход по всей длине для фиксации эскиза.", en: "Light pass along the whole length to set the sketch." },
    path: [
      [10, 44], [20, 36], [30, 29], [40, 24], [50, 22], [60, 23],
      [70, 27], [80, 33], [90, 40],
    ],
  },
];
